import { useState, useEffect } from 'react'

/**
 * usePrefersReducedMotion — subscribes to the `(prefers-reduced-motion: reduce)`
 * media query and returns a boolean indicating whether the user prefers reduced motion.
 *
 * Returns `false` when `window` is undefined (SSR safety).
 * Registers and cleans up the `change` listener on mount/unmount.
 *
 * Reuse note: This is the canonical motion-preference utility for the codebase.
 * Epic 7 chart.js MRR charts must import from this module, not reimplement it.
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(() => {
    /* istanbul ignore next */
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    /* istanbul ignore next */
    if (typeof window === 'undefined') return

    const mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches)
    }

    mediaQueryList.addEventListener('change', handler)

    return () => {
      mediaQueryList.removeEventListener('change', handler)
    }
  }, [])

  return prefersReducedMotion
}
